/**
 * `libraryDiff.ts` — a value-level comparison between a placed model and its
 * asset-library entry, in either direction.
 *
 * **One comparison, two readings.** Save to Library pushes the project's state
 * up; the update prompt pulls the library's state down. Both are asking about
 * exactly the same pairs of values, so they share this function and differ only
 * in which side is labelled `from` and which `to`. Two separate diffs would
 * drift the moment a field was added to one of them.
 *
 * The comparison surface is the BAKE SURFACE — what Save to Library writes onto
 * the part rows, and therefore the only thing an update can ever carry: per-part
 * transforms (never the root's), visibility, names, aliases and materials.
 * Geometry is not on it; the GLB is never rewritten in place.
 *
 * Rows are fetched FRESH rather than read from the retained catalogs. Those hold
 * transforms, materials and aliases but not names or visibility, and both
 * callers are user-initiated dialogs where one read is cheap and staleness would
 * show up as a wrong number in front of the user.
 */

import {
  DEFAULT_ALIAS_LANGUAGE,
  readAliases,
  type PartAlias,
} from "@models/data/aliasData";
import type { EntitySnapshot } from "@models/data/entityData";
import {
  materialColorAlpha,
  materialColorHex,
  materialNumber,
  type MaterialDto,
} from "@models/data/materialDto";
import { type PartDto, partMaterialIds, partTransform } from "@models/data/partDto";
import { vec3From } from "@models/data/nameFloatMap";
import type { IStorageModel } from "@models/storage/IStorageModel";

/**
 * The three kinds of change a save can carry.
 *
 * `other` is everything that is neither a material nor an alias — names,
 * visibility, per-part transforms. The bake writes those together, which is
 * why they are one group rather than three.
 */
export type LibraryDiffGroup = "materials" | "aliases" | "other";

/** Reading order: structure, then names, then look. */
export const LIBRARY_DIFF_GROUPS: ReadonlyArray<LibraryDiffGroup> = [
  "other",
  "aliases",
  "materials",
];

export const LIBRARY_DIFF_GROUP_LABELS: Readonly<Record<LibraryDiffGroup, string>> = {
  other: "Names & placement",
  aliases: "Aliases",
  materials: "Materials",
};

/**
 * The unit a save is chosen by: one part's changes within one group.
 *
 * Finer than the group, so one part can be left alone; coarser than the field,
 * because the bake writes a part's name, visibility and transform in one go —
 * a field-level key would promise a granularity the writer cannot honour.
 */
export function libraryDiffKey(partId: string, group: LibraryDiffGroup): string {
  return `${partId}|${group}`;
}

/**
 * What a sync must NOT carry, as {@link libraryDiffKey} keys. Empty = carry
 * everything, which is what both directions did before the review sheet.
 *
 * **Deselection is the payload, and that is deliberate.** The natural framing
 * is a set of things to write — but the caller is a dialog that only knows
 * about the differences it LISTED, and both writers touch fields the diff does
 * not compare (a part's scale, its merge flag, a material's clear coat). An
 * inclusion set would silently stop carrying those.
 *
 * Not selected still never means cleared: an excluded group is simply not
 * written, so the receiving side keeps whatever it already held.
 */
export type LibrarySyncExclusions = ReadonlySet<string>;

/** The empty exclusion set — carry everything. */
export const NOTHING_EXCLUDED: LibrarySyncExclusions = new Set<string>();

export interface LibraryDiffEntry {
  /** The part row this change belongs to — the addressable half of the key. */
  readonly partId: string;
  /** The part this row is about, as the user sees it named. */
  readonly partLabel: string;
  readonly group: LibraryDiffGroup;
  /** "Name", "Visible", "Position", "Material", `Alias (de)` … */
  readonly field: string;
  readonly from: string;
  readonly to: string;
}

export interface LibraryDiff {
  readonly entries: ReadonlyArray<LibraryDiffEntry>;
  readonly materials: number;
  readonly aliases: number;
  readonly other: number;
}

export const EMPTY_LIBRARY_DIFF: LibraryDiff = {
  entries: [], materials: 0, aliases: 0, other: 0,
};

/**
 * `push` = Save to Library: the library's current value on the left, this
 * project's on the right — "this is what I am about to publish".
 * `pull` = the update prompt: this project's value on the left, the library's
 * on the right — "this is what I am about to adopt".
 */
export type LibraryDiffDirection = "push" | "pull";

export interface LibraryDiffInput {
  readonly storage: IStorageModel;
  readonly customerId: string;
  readonly assetId: string;
  readonly snapshots: ReadonlyArray<EntitySnapshot>;
  /** This project's material rows, by id (the model's cache). */
  readonly ownMaterial: (materialId: string) => MaterialDto | null;
  readonly direction: LibraryDiffDirection;
  readonly signal?: AbortSignal;
}

export async function buildLibraryDiff(input: LibraryDiffInput): Promise<LibraryDiff> {
  const { storage, customerId, assetId, snapshots, ownMaterial, direction, signal } = input;

  let parts: ReadonlyArray<PartDto>;
  try {
    parts = await storage.getParts(customerId, assetId, signal);
  } catch {
    return EMPTY_LIBRARY_DIFF;
  }
  const partById = new Map<string, PartDto>();
  for (const part of parts) {
    if (part.ID) partById.set(part.ID, part);
  }
  if (partById.size === 0) return EMPTY_LIBRARY_DIFF;

  // One read for every library material this asset names.
  const libraryMaterials = new Map<string, MaterialDto>();
  const libIds = [...new Set(parts.flatMap((p) => partMaterialIds(p)).filter(Boolean))];
  if (libIds.length > 0) {
    try {
      for (const row of await storage.getMaterialBatch(customerId, libIds, signal)) {
        if (row?.ID) libraryMaterials.set(row.ID, row);
      }
    } catch {
      /* materials simply go uncompared — better than an invented row */
    }
  }

  const entries: LibraryDiffEntry[] = [];
  const push = (
    part: { readonly id: string; readonly label: string },
    group: LibraryDiffGroup,
    field: string,
    library: string,
    project: string,
  ): void => {
    if (library === project) return;
    const at = { partId: part.id, partLabel: part.label, group, field };
    entries.push(
      direction === "push"
        ? { ...at, from: library, to: project }
        : { ...at, from: project, to: library },
    );
  };

  for (const snapshot of walk(snapshots)) {
    const part = snapshot.partId ? partById.get(snapshot.partId) : undefined;
    if (!part) continue;
    // Carried as a pair: the id addresses the part row a save writes, the label
    // is what the user reads.
    const label = {
      id: part.ID as string,
      label: snapshot.displayName || snapshot.name || "Part",
    };

    const libName = typeof part.DisplayName === "string" && part.DisplayName
      ? part.DisplayName
      : (typeof part.Name === "string" ? part.Name : "");
    const ownName = snapshot.displayName || snapshot.name || "";
    if (libName) push(label, "other", "Name", libName, ownName);

    push(
      label, "other", "Visible",
      part.Visibility !== false ? "shown" : "hidden",
      snapshot.visibility ? "shown" : "hidden",
    );


    // The root's transform is the placement, not the model — Save to Library
    // never bakes it and a pull never adopts it, so it is never a difference.
    if (!snapshot.isRoot) {
      const t = partTransform(part);
      push(label, "other", "Position",
        formatVec("pos", vec3From(t.position, [0, 0, 0])),
        formatVec("pos", snapshot.transform.position));
      push(label, "other", "Rotation",
        formatVec("rot", vec3From(t.rotation, [0, 0, 0])),
        formatVec("rot", snapshot.transform.rotation));
    }

    // Aliases are per language, so the row is per language — a whole-list
    // compare would say "aliases changed" and name nothing.
    const libAliases = readAliases(part.Aliases);
    const ownAliases = snapshot.aliases;
    for (const language of languageUnion(libAliases, ownAliases)) {
      push(
        label, "aliases", `Alias (${language})`,
        aliasValue(libAliases, language),
        aliasValue(ownAliases, language),
      );
    }

    // Materials compare by VALUE, never by id: Save to Library mints a fresh
    // library material id on EVERY save whether or not anything changed, so
    // the ids always differ and would always report a change.
    const partMats = partMaterialIds(part);
    if (partMats.length === snapshot.materialIds.length) {
      for (let i = 0; i < partMats.length; i++) {
        const lib = libraryMaterials.get(partMats[i]);
        const own = ownMaterial(snapshot.materialIds[i]);
        if (!lib || !own) continue;
        pushMaterialChanges(push, label, lib, own);
      }
    }
  }

  return {
    entries,
    materials: entries.filter((e) => e.group === "materials").length,
    aliases: entries.filter((e) => e.group === "aliases").length,
    other: entries.filter((e) => e.group === "other").length,
  };
}

/**
 * A material comparison, property by property.
 *
 * Two shapes, because a material can change in two different ways and they are
 * not the same news:
 *
 *  - **A different material was applied.** The name is the change; listing its
 *    properties would describe the new material rather than what happened.
 *  - **The same material was edited.** Then only the properties that actually
 *    moved are worth a line — `push` drops equal pairs, so a colour change
 *    prints one colour row and nothing else.
 *
 * Not every field is compared. A change to something outside this set (clear
 * coat, tiling) still PUBLISHES — the diff is a summary of what a person would
 * recognize, not the contract for what a save writes.
 */
function pushMaterialChanges(
  push: (
    part: { readonly id: string; readonly label: string },
    group: LibraryDiffGroup,
    field: string,
    library: string,
    project: string,
  ) => void,
  part: { readonly id: string; readonly label: string },
  lib: MaterialDto,
  own: MaterialDto,
): void {
  const libName = materialName(lib);
  const ownName = materialName(own);
  if (libName !== ownName) {
    push(part, "materials", "Material", libName, ownName);
    return;
  }
  push(part, "materials", "Color", colorOf(lib.BaseColor, "#ffffff"), colorOf(own.BaseColor, "#ffffff"));
  push(part, "materials", "Emission", colorOf(lib.EmissionColor, "#000000"), colorOf(own.EmissionColor, "#000000"));
  push(part, "materials", "Metallic", percent("metal", lib.Metallic, 0), percent("metal", own.Metallic, 0));
  push(part, "materials", "Smoothness", percent("smooth", lib.Smoothness, 0), percent("smooth", own.Smoothness, 0));
  push(
    part, "materials", "Opacity",
    `opacity ${Math.round(materialColorAlpha(lib.BaseColor, 1) * 100)}%`,
    `opacity ${Math.round(materialColorAlpha(own.BaseColor, 1) * 100)}%`,
  );
}

function materialName(m: MaterialDto): string {
  return (typeof m.Name === "string" ? m.Name.trim() : "") || "Untitled material";
}

function colorOf(value: unknown, fallback: string): string {
  return materialColorHex(value, fallback);
}

function percent(word: string, value: unknown, fallback: number): string {
  return `${word} ${Math.round(materialNumber(value, fallback) * 100)}%`;
}

/**
 * Values are written to be read WITHOUT a field label beside them (user
 * direction 2026-08-21 — every row saying "Material" was noise), so the unit
 * word rides the value the way `metal 0%` already did. `pos` / `rot` are what
 * keeps two vector rows on the same part apart.
 */
function formatVec(word: string, v: readonly [number, number, number]): string {
  const n = (x: number): string => (Math.round(x * 1000) / 1000).toString();
  return `${word} ${n(v[0])}, ${n(v[1])}, ${n(v[2])}`;
}

function languageUnion(
  a: ReadonlyArray<PartAlias>,
  b: ReadonlyArray<PartAlias>,
): string[] {
  return [...new Set([...a, ...b].map((x) => x.language))].sort();
}

/**
 * An alias, tagged with its language only when that is not the default — every
 * alias authored in this client is `en`, so printing it would be noise, while
 * one arriving from elsewhere needs it to be legible.
 */
function aliasValue(list: ReadonlyArray<PartAlias>, language: string): string {
  const value = list.find((x) => x.language === language)?.value;
  if (!value) return "—";
  return language === DEFAULT_ALIAS_LANGUAGE ? value : `${language}: ${value}`;
}

function* walk(snapshots: ReadonlyArray<EntitySnapshot>): Generator<EntitySnapshot> {
  for (const snapshot of snapshots) {
    yield snapshot;
    yield* walk(snapshot.children);
  }
}
